import { InMemoryCache } from '@apollo/client';

/** The app's Apollo cache: its type policies decide how lists merge and how records are keyed. */
export const createCache = () =>
  new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          listFoods: {
            keyArgs: ['kitchen', 'date', 'shift', 'brand', 'limit'],
            merge(existing, incoming, { args, readField }) {
              if (!args?.next) return incoming;

              const seen = new Set<string>();
              const data: any[] = [];
              for (const food of [...(existing?.data ?? []), ...(incoming?.data ?? [])]) {
                const id = readField('id', food) as string;
                if (seen.has(id)) continue;
                seen.add(id);
                data.push(food);
              }
              return { ...incoming, data, next: incoming?.next ?? null };
            }
          },
          listFoodComponentsByName: {
            keyArgs: ['kitchen', 'brand', 'name', 'ingredientId', 'limit'],
            merge(existing, incoming, { args, readField }) {
              if (!args?.next) return incoming;

              const seen = new Set<string>();
              const data: any[] = [];
              for (const foodComponent of [...(existing?.data ?? []), ...(incoming?.data ?? [])]) {
                const id = readField('id', foodComponent) as string;
                if (seen.has(id)) continue;
                seen.add(id);
                data.push(foodComponent);
              }
              return { ...incoming, data, next: incoming?.next ?? null };
            }
          },
          listFoodComponentByRange: {
            keyArgs: ['kitchen', 'startDate', 'endDate', 'filters', 'limit'],
            merge(existing, incoming, { args, readField }) {
              if (!args?.next) return incoming;

              const seen = new Set<string>();
              const components: any[] = [];
              for (const c of [...(existing?.components ?? []), ...(incoming?.components ?? [])]) {
                const id = readField('id', c) as string;
                if (seen.has(id)) continue;
                seen.add(id);
                components.push(c);
              }
              return { ...incoming, components, next: incoming?.next ?? null };
            }
          },
          listBaggingDispatchByRange: {
            keyArgs: ['kitchen', 'startDate', 'endDate', 'limit'],
            merge(existing, incoming, { args, readField }) {
              if (!args?.next) return incoming;

              const seen = new Set<string>();
              const dispatches: any[] = [];
              for (const d of [...(existing?.dispatches ?? []), ...(incoming?.dispatches ?? [])]) {
                const id = readField('id', d) as string;
                const session = readField('session', d) as string;
                const key = `${id}:${session}`;
                if (seen.has(key)) continue;
                seen.add(key);
                dispatches.push(d);
              }
              return { ...incoming, dispatches, next: incoming?.next ?? null };
            }
          }
        }
      },
      ComponentsQuantity: {
        keyFields: ['id', 'quantity']
      },
      Food: {
        keyFields: ['id', 'kitchen', 'date']
      },
      FoodPortioningRecord: {
        keyFields: ['id', 'kitchen', 'day', 'shift', 'variant']
      },
      BaggingDispatchRecord: {
        keyFields: ['id', 'kitchen', 'day', 'shift']
      },
      PortioningStatus: {
        keyFields: ['id', 'session']
      },
      ProcessTime: {
        keyFields: ['id']
      },
      PausedInterval: {
        keyFields: ['id']
      },
      FoodComponent: {
        keyFields: ['id', 'kitchen', 'date', 'quantity', 'weight']
      },
      CookingStations: {
        keyFields: ['id', 'name', 'status', 'endTime']
      },
      SingleFoodComponent: {
        keyFields: ['id', 'kitchen', 'weight']
      },
      FoodComponentQualityStandard: {
        keyFields: ['id', 'name', 'description']
      },
      BaggingDispatchCity: {
        keyFields: ['id', 'name', 'total', 'modified', 'modifications']
      },
      ComponentShortage: {
        keyFields: ['id', 'cookingStations', 'foodName', 'weight']
      },
      KitchenWastageInputs: {
        keyFields: ['preppedWeight', 'chilledWeight', 'wastage', 'action']
      },
      BaggingDispatch: {
        keyFields: ['id', 'session']
      },
      Ingredient: {
        keyFields: ['id', 'quantity']
      },
      Complaint: {
        keyFields: ['id', 'kitchen', 'date']
      },
      Gift: {
        keyFields: ['id', 'kitchen', 'date']
      },
      QualityMetric: {
        keyFields: ['id', 'kitchen', 'day', 'shift']
      },
      QualityMetricFood: {
        keyFields: ['id', 'cacheKey']
      },
      QualityMetricsFoodComponent: {
        keyFields: ['id', 'cacheKey']
      },
      ComponentLeftoverStats: {
        keyFields: ['id', 'date', 'kitchen', 'stations']
      },
      ComponentLeftoverStatsByStation: {
        keyFields: ['name', 'leftovers', 'totalLeftoverCost']
      },
      NotificationStats: {
        keyFields: ['id', 'kitchen', 'date']
      },
      DeliveryTicket: {
        keyFields: ['id', 'kitchen', 'date']
      },
      ProductivityMetricsFood: {
        keyFields: ['id', 'cacheKey']
      },
      FoodMetrics: {
        keyFields: ['cacheKey']
      },
      FoodComponentYieldBatchIngredient: {
        keyFields: ['id', 'name', ['en']]
      },
      StaffTracking: {
        keyFields: ['id']
      }
    }
  });
